/**
 * Configurazione dei risparmi per slider_all_bonus
 * 
 * NUOVA VERSIONE: Usa il database JSON dei CAP per ottenere i valori dinamici
 * invece delle configurazioni hardcoded.
 */

import { 
  ProductType,
  BonusDisplayItem,
} from '../types';
import { formatCurrency } from '../utils/helpers';
import { 
  getBonusesForCap, 
  getTooltipTextForProduct,
  formatBonusForDisplay,
} from './bonusLookup';
import { getBenefitKeyForSlide } from './slidesConfig';

/**
 * Mappa gli indici delle slide ai nomi dei benefit
 * - decision_stage (install_timeline) -> indice 0
 * - name_form -> indice 1
 * - email_form -> indice 2
 * - phone_form -> indice 3
 */
const SLIDE_TO_BONUS_INDEX: Record<string, number> = {
  install_timeline: 0,
  decision_stage: 0,
  name_form: 1,
  email_form: 2,
  phone_form: 3,
};

/**
 * Ottiene il testo del tooltip in base al prodotto
 */
export function getTooltipText(productType?: ProductType): string {
  return getTooltipTextForProduct(productType);
}

/**
 * Ottiene il testo del beneficio per una slide specifica
 * Usa il database CAP per valori dinamici
 * 
 * @param slideId - ID della slide (decision_stage, name_form, email_form, phone_form)
 * @param productType - Tipo di prodotto
 * @param _zipFlag - (deprecato, mantenuto per compatibilità)
 * @param zipCode - Codice CAP per il lookup
 * @returns Testo formattato del bonus o null
 */
export function getSavingsText(
  slideId: string,
  productType?: ProductType,
  _zipFlag?: boolean | null,
  zipCode?: string
): string | null {
  // Mappa l'ID slide alla chiave del beneficio
  const benefitKey = getBenefitKeyForSlide(slideId);
  const bonusIndex = SLIDE_TO_BONUS_INDEX[benefitKey];
  
  if (bonusIndex === undefined) return null;
  
  // Ottieni i bonus dal database CAP
  const bonuses = getBonusesForCap(zipCode, productType);
  const bonus = bonuses.slideBonus[bonusIndex];
  
  if (!bonus) return null;
  
  return formatBonusForDisplay(bonus.value, bonus.label);
}

/**
 * Calcola il risparmio totale cumulativo fino a una slide
 * 
 * @param slideId - ID della slide
 * @param productType - Tipo di prodotto
 * @param _zipFlag - (deprecato)
 * @param zipCode - Codice CAP
 * @returns Stringa formattata del totale cumulativo
 */
export function calculateTotalSavings(
  slideId: string,
  productType?: ProductType,
  _zipFlag?: boolean | null,
  zipCode?: string
): string {
  const benefitKey = getBenefitKeyForSlide(slideId);
  const currentIndex = SLIDE_TO_BONUS_INDEX[benefitKey];
  
  if (currentIndex === undefined) return '';
  
  const bonuses = getBonusesForCap(zipCode, productType);
  
  // Somma i bonus fino all'indice corrente
  let total = 0;
  for (let i = 0; i <= currentIndex && i < bonuses.slideBonus.length; i++) {
    total += bonuses.slideBonus[i].value;
  }
  
  return formatCurrency(total);
}

/**
 * Calcola il conteggio bonus e totale per il feedback CAP
 * Usato nella slide del CAP per mostrare "X bonus disponibili"
 */
export function calculateBonusSummary(
  productType?: ProductType,
  _zipFlag?: boolean | null,
  zipCode?: string
): { bonusCount: number; totalSavings: number } {
  const bonuses = getBonusesForCap(zipCode, productType);
  
  return {
    bonusCount: bonuses.bonusCount + 1, // +1 per "Guadagni Trading Energia"
    totalSavings: bonuses.total,
  };
}

/**
 * Interfaccia per un item del riepilogo risparmi
 */
export interface SavingsSummaryItem {
  label: string;
  value: string;
  scadenza?: string | null;
  category?: 'PV' | 'HP';
}

/**
 * Genera i dati per il riepilogo finale dei risparmi
 * Usa tutti i bonus disponibili dal database CAP
 */
export function generateSavingsSummary(
  productType?: ProductType,
  _zipFlag?: boolean | null,
  zipCode?: string
): {
  items: SavingsSummaryItem[];
  total: number;
  co2Saved: string;
} {
  const bonuses = getBonusesForCap(zipCode, productType);
  
  // Converti i bonus in items per il riepilogo
  const items: SavingsSummaryItem[] = bonuses.summaryBonus.map((bonus: BonusDisplayItem) => ({
    label: bonus.label,
    value: formatCurrency(bonus.value),
    scadenza: bonus.scadenza,
    category: bonus.category,
  }));
  
  return {
    items,
    total: bonuses.total,
    co2Saved: 'Scoprilo in chiamata',
  };
}

// ============================================
// Esportazioni per retrocompatibilità
// (mantenute per evitare breaking changes in altri moduli)
// ============================================

/**
 * @deprecated Usare getTooltipText invece
 */
export const TOOLTIP_TEXTS = {
  Photovoltaic: getTooltipTextForProduct('Photovoltaic'),
  Heatpump: getTooltipTextForProduct('Heatpump'),
  Photovoltaic_Heatpump: getTooltipTextForProduct('Photovoltaic + Heatpump'),
};
